import {useNavigate} from '@github-ui/react-router'
import {SegmentedControl} from '@primer/react'
import {useCallback} from 'react'

export type ProjectsTemplatesMode = 'projects' | 'templates'

interface ProjectsTemplatesToggleProps {
  mode: ProjectsTemplatesMode
  /** Path to the projects index, e.g. `/monalisa/test-repo/projects`. */
  baseUrl: string
}

/**
 * Switches the repo projects page between the projects and templates views.
 *
 * Navigates to a clean `${baseUrl}` or `${baseUrl}/templates` — other URL state (`?q=`,
 * `?sort=`, `?state=`) is intentionally not preserved.
 *
 * The `variant={{narrow: 'dropdown'}}` prop tells Primer's `SegmentedControl` to swap
 * its segments for an `ActionMenu` on narrow viewports.
 */
export function ProjectsTemplatesToggle({mode, baseUrl}: ProjectsTemplatesToggleProps) {
  const navigate = useNavigate()

  const goToProjects = useCallback(() => {
    if (mode === 'projects') return
    navigate(baseUrl)
  }, [baseUrl, mode, navigate])

  const goToTemplates = useCallback(() => {
    if (mode === 'templates') return
    navigate(`${baseUrl}/templates`)
  }, [baseUrl, mode, navigate])

  return (
    <SegmentedControl aria-label="Project type" variant={{narrow: 'dropdown'}}>
      <SegmentedControl.Button selected={mode === 'projects'} onClick={goToProjects}>
        All
      </SegmentedControl.Button>
      <SegmentedControl.Button selected={mode === 'templates'} onClick={goToTemplates}>
        Templates
      </SegmentedControl.Button>
    </SegmentedControl>
  )
}
